import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Alert,
  Divider,
  FormHelperText,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import {
  ServicePackageFormData,
  CPTCodeSelection,
  ICD10CodeSelection,
  HCPCSCodeSelection,
} from '../../types/servicePackage'
import { servicePackageService } from '../../services/servicePackageService'
import { getFieldError } from '../../utils/servicePackageValidation'

interface Section2ClinicalCodingProps {
  formData: ServicePackageFormData
  onUpdate: (data: Partial<ServicePackageFormData>) => void
  errors: any
}

export const Section2ClinicalCoding: React.FC<Section2ClinicalCodingProps> = ({
  formData,
  onUpdate,
  errors,
}) => {
  const [cptOptions, setCptOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [icdOptions, setIcdOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [hcpcsOptions, setHcpcsOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [modifierOptions, setModifierOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [loadingCpt, setLoadingCpt] = useState(false)
  const [loadingIcd, setLoadingIcd] = useState(false)
  const [loadingHcpcs, setLoadingHcpcs] = useState(false)
  const [loadingModifiers, setLoadingModifiers] = useState(false)
  const [cptFetchError, setCptFetchError] = useState<string | null>(null)
  const [icdFetchError, setIcdFetchError] = useState<string | null>(null)
  const [hcpcsFetchError, setHcpcsFetchError] = useState<string | null>(null)
  const [modifierFetchError, setModifierFetchError] = useState<string | null>(
    null
  )

  const [cptInputValue, setCptInputValue] = useState('')
  const [icdInputValue, setIcdInputValue] = useState('')
  const [hcpcsInputValue, setHcpcsInputValue] = useState('')
  const [modifierInputValue, setModifierInputValue] = useState('')
  const [icdSearch, setIcdSearch] = useState('')
  const [selectedModifierOption, setSelectedModifierOption] = useState<{
    id: string
    code: string
    description: string
  } | null>(null)

  // Place of Service options
  const posOptions = [
    { value: '11', label: 'Office' },
    { value: '12', label: 'Home' },
    { value: '10', label: 'Telehealth' },
  ]

  useEffect(() => {
    let isMounted = true
    setLoadingCpt(true)

    const handler = window.setTimeout(async () => {
      try {
        const result = await servicePackageService.fetchCptCodes()
        if (isMounted) {
          setCptOptions(result)
          setCptFetchError(null)
        }
      } catch (error) {
        console.error('Failed to load CPT codes', error)
        if (isMounted) {
          setCptFetchError('Unable to load CPT codes. Please try again later.')
        }
      } finally {
        if (isMounted) {
          setLoadingCpt(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    setLoadingIcd(true)

    const handler = window.setTimeout(async () => {
      try {
        const result = await servicePackageService.fetchIcdCodes(icdSearch)
        if (isMounted) {
          setIcdOptions(result)
          setIcdFetchError(null)
        }
      } catch (error) {
        console.error('Failed to load ICD-10 codes', error)
        if (isMounted) {
          setIcdFetchError(
            'Unable to load ICD-10 codes. Please try again later.'
          )
        }
      } finally {
        if (isMounted) {
          setLoadingIcd(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [icdSearch])

  useEffect(() => {
    let isMounted = true
    setLoadingHcpcs(true)

    const handler = window.setTimeout(async () => {
      try {
        const result = await servicePackageService.fetchHCPCSCodes()
        if (isMounted) {
          setHcpcsOptions(result)
          setHcpcsFetchError(null)
        }
      } catch (error) {
        console.error('Failed to load HCPCS codes', error)
        if (isMounted) {
          setHcpcsFetchError(
            'Unable to load HCPCS codes. Please try again later.'
          )
        }
      } finally {
        if (isMounted) {
          setLoadingHcpcs(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    setLoadingModifiers(true)

    const handler = window.setTimeout(async () => {
      try {
        const result = await servicePackageService.fetchModifiers()
        if (isMounted) {
          setModifierOptions(result)
          setModifierFetchError(null)
        }
      } catch (error) {
        console.error('Failed to load Modifiers', error)
        if (isMounted) {
          setModifierFetchError(
            'Unable to load Modifiers. Please try again later.'
          )
        }
      } finally {
        if (isMounted) {
          setLoadingModifiers(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [])

  // Initialize modifier option when formData.modifier is set and options are loaded
  useEffect(() => {
    if (formData.clinicalCoding.modifier && modifierOptions.length > 0) {
      // Check if current selected option matches the formData modifier
      const currentMatches =
        selectedModifierOption?.id === formData.clinicalCoding.modifier ||
        selectedModifierOption?.code === formData.clinicalCoding.modifier

      // Only update if there's no match or if the selected option doesn't match
      if (!currentMatches) {
        // Try to find by ID first, then by code
        const foundModifier =
          modifierOptions.find(
            option => option.id === formData.clinicalCoding.modifier
          ) ||
          modifierOptions.find(
            option => option.code === formData.clinicalCoding.modifier
          )
        if (foundModifier) {
          setSelectedModifierOption(foundModifier)
          setModifierInputValue(
            foundModifier.description
              ? `${foundModifier.code} - ${foundModifier.description}`
              : foundModifier.code
          )
        }
      }
    } else if (!formData.clinicalCoding.modifier && selectedModifierOption) {
      setSelectedModifierOption(null)
      setModifierInputValue('')
    }
  }, [
    formData.clinicalCoding.modifier,
    modifierOptions,
    selectedModifierOption,
  ])

  const addCptCode = (code: CPTCodeSelection) => {
    // Check if code is already selected
    const isAlreadySelected = formData.clinicalCoding.cptCodes.some(
      selected => {
        const codeIdMatch =
          selected.codeId && code.codeId && selected.codeId === code.codeId
        const codeMatch =
          selected.code && code.code && selected.code === code.code
        return codeIdMatch || codeMatch
      }
    )

    if (isAlreadySelected) return

    const updatedCptCodes = [
      ...formData.clinicalCoding.cptCodes,
      {
        id: `${Date.now()}-${Math.random()}`,
        codeId: code.codeId,
        code: code.code,
        description: code.description || '',
        quantity: code.quantity || 1,
      },
    ]

    onUpdate({
      clinicalCoding: {
        ...formData.clinicalCoding,
        cptCodes: updatedCptCodes,
      },
    })
  }

  const removeCptCode = (index: number) => {
    const updatedCptCodes = formData.clinicalCoding.cptCodes.filter(
      (_, i) => i !== index
    )
    onUpdate({
      clinicalCoding: {
        ...formData.clinicalCoding,
        cptCodes: updatedCptCodes,
      },
    })
  }

  const addIcd10Code = (code: ICD10CodeSelection) => {
    // Check if code is already selected
    const isAlreadySelected = formData.clinicalCoding.icd10Codes.some(
      selected => {
        // Match by codeId if both exist
        if (selected.codeId && code.codeId) {
          return selected.codeId === code.codeId
        }
        // Match by code as fallback or primary identifier
        return selected.code === code.code
      }
    )

    if (formData.clinicalCoding.icd10Codes.length < 10 && !isAlreadySelected) {
      const updatedIcd10Codes = [...formData.clinicalCoding.icd10Codes, code]
      onUpdate({
        clinicalCoding: {
          ...formData.clinicalCoding,
          icd10Codes: updatedIcd10Codes,
        },
      })
    }
  }

  const removeIcd10Code = (index: number) => {
    const updatedIcd10Codes = formData.clinicalCoding.icd10Codes.filter(
      (_, i) => i !== index
    )
    onUpdate({
      clinicalCoding: {
        ...formData.clinicalCoding,
        icd10Codes: updatedIcd10Codes,
      },
    })
  }

  const addHCPCSCode = (code: HCPCSCodeSelection) => {
    // Check if code is already selected
    const isAlreadySelected = formData.clinicalCoding.hcpcsLevel.some(
      selected => {
        // Match by codeId if both exist
        if (selected.codeId && code.codeId) {
          return selected.codeId === code.codeId
        }
        // Match by code as fallback or primary identifier
        return selected.code === code.code
      }
    )

    if (!isAlreadySelected) {
      const updatedHcpcsCodes = [...formData.clinicalCoding.hcpcsLevel, code]
      onUpdate({
        clinicalCoding: {
          ...formData.clinicalCoding,
          hcpcsLevel: updatedHcpcsCodes,
        },
      })
      setHcpcsInputValue('')
    }
  }

  const removeHCPCSCode = (index: number) => {
    const updatedHcpcsCodes = formData.clinicalCoding.hcpcsLevel.filter(
      (_, i) => i !== index
    )
    onUpdate({
      clinicalCoding: {
        ...formData.clinicalCoding,
        hcpcsLevel: updatedHcpcsCodes,
      },
    })
  }

  const handleModifierChange = (
    value: {
      id: string
      code: string
      description: string
    } | null
  ) => {
    if (value) {
      setSelectedModifierOption(value)
      onUpdate({
        clinicalCoding: {
          ...formData.clinicalCoding,
          modifier: value.id, // Store the ID
        },
      })
      setModifierInputValue(
        value.description ? `${value.code} - ${value.description}` : value.code
      )
    } else {
      setSelectedModifierOption(null)
      onUpdate({
        clinicalCoding: {
          ...formData.clinicalCoding,
          modifier: '',
        },
      })
      setModifierInputValue('')
    }
  }

  const handlePosChange = (value: string) => {
    onUpdate({
      clinicalCoding: {
        ...formData.clinicalCoding,
        pos: value,
      },
    })
  }

  const cptCodeError = getFieldError(errors, 'cptCodes')
  const icd10CodeError = getFieldError(errors, 'icd10Codes')
  const medicalNecessityError = getFieldError(errors, 'medicalNecessityNote')

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Clinical Coding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define the clinical codes and medical necessity for your service
        package.
      </Typography>

      {/* CPT Codes Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent id="cpt-codes-section">
          <Typography variant="h6" gutterBottom>
            CPT Codes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add the Current Procedural Terminology codes for your services. At
            least one CPT code is required.
          </Typography>

          {cptCodeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cptCodeError}
            </Alert>
          )}

          {cptFetchError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {cptFetchError}
            </Alert>
          )}

          {/* Add New CPT Code */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={cptOptions.filter(option => {
                  // Filter out already selected CPT codes
                  return !formData.clinicalCoding.cptCodes.some(selected => {
                    const codeIdMatch =
                      selected.codeId &&
                      option.id &&
                      selected.codeId === option.id
                    const codeMatch =
                      selected.code &&
                      option.code &&
                      selected.code === option.code
                    return codeIdMatch || codeMatch
                  })
                })}
                getOptionLabel={option =>
                  option.description
                    ? `${option.code} - ${option.description}`
                    : option.code
                }
                value={null}
                inputValue={cptInputValue}
                onChange={(_, value) => {
                  if (value) {
                    addCptCode({
                      id: `${Date.now()}-${Math.random()}`,
                      codeId: value.id,
                      code: value.code,
                      description:
                        value.description || `CPT Code ${value.code}`,
                      quantity: 1,
                    })
                    setCptInputValue('')
                  }
                }}
                onInputChange={(_, value) => {
                  setCptInputValue(value)
                }}
                loading={loadingCpt}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false
                  return option.code === value.code && option.id === value.id
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select CPT Code"
                    placeholder="Search CPT codes..."
                    error={!!cptCodeError}
                    helperText={cptCodeError}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Selected CPT Codes */}
          {formData.clinicalCoding.cptCodes.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected CPT Codes:
              </Typography>
              {formData.clinicalCoding.cptCodes.map((cptCode, index) => (
                <Card
                  key={cptCode.id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{cptCode.code}</Typography>
                    {cptCode.description && (
                      <Typography variant="body2" color="text.secondary">
                        {cptCode.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      onClick={() => removeCptCode(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ICD-10 Codes Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent id="icd-codes-section">
          <Typography variant="h6" gutterBottom>
            ICD-10 Codes (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add International Classification of Diseases codes. Maximum 10 codes
            allowed.
          </Typography>

          {icd10CodeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {icd10CodeError}
            </Alert>
          )}

          {icdFetchError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {icdFetchError}
            </Alert>
          )}

          {/* Add ICD-10 Code */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={icdOptions.filter(option => {
                  // Filter out already selected ICD-10 codes
                  return !formData.clinicalCoding.icd10Codes.some(selected => {
                    // Match by codeId if both exist
                    if (selected.codeId && option.id) {
                      return selected.codeId === option.id
                    }
                    // Match by code as fallback or primary identifier
                    return selected.code === option.code
                  })
                })}
                getOptionLabel={option =>
                  option.description
                    ? `${option.code} - ${option.description}`
                    : option.code
                }
                value={null}
                inputValue={icdInputValue}
                onChange={(_, value) => {
                  if (value) {
                    addIcd10Code({
                      id: `${Date.now()}-${Math.random()}`,
                      codeId: value.id,
                      code: value.code,
                      description:
                        value.description || `ICD-10 Code ${value.code}`,
                    })
                    setIcdInputValue('')
                    setIcdSearch('')
                  }
                }}
                onInputChange={(_, value) => {
                  setIcdInputValue(value)
                  setIcdSearch(value)
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select ICD-10 Code"
                    placeholder="Search ICD-10 codes..."
                    error={!!icd10CodeError}
                    helperText={icd10CodeError}
                  />
                )}
                loading={loadingIcd}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                disabled={formData.clinicalCoding.icd10Codes.length >= 10}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                {formData.clinicalCoding.icd10Codes.length} / 10 codes selected
              </Typography>
            </Grid>
          </Grid>

          {/* Selected ICD-10 Codes */}
          {formData.clinicalCoding.icd10Codes.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected ICD-10 Codes:
              </Typography>
              {formData.clinicalCoding.icd10Codes.map((icd10Code, index) => (
                <Card
                  key={icd10Code.id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">
                      {icd10Code.code}
                    </Typography>
                    {icd10Code.description && (
                      <Typography variant="body2" color="text.secondary">
                        {icd10Code.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      onClick={() => removeIcd10Code(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* HCPCS Level II Codes Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent id="hcpcs-codes-section">
          <Typography variant="h6" gutterBottom>
            HCPCS Level II Codes (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add Healthcare Common Procedure Coding System Level II codes.
          </Typography>

          {hcpcsFetchError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {hcpcsFetchError}
            </Alert>
          )}

          {/* Add HCPCS Code */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={hcpcsOptions.filter(option => {
                  // Filter out already selected HCPCS codes
                  return !formData.clinicalCoding.hcpcsLevel.some(selected => {
                    // Match by codeId if both exist
                    if (selected.codeId && option.id) {
                      return selected.codeId === option.id
                    }
                    // Match by code as fallback or primary identifier
                    return selected.code === option.code
                  })
                })}
                getOptionLabel={option =>
                  option.description
                    ? `${option.code} - ${option.description}`
                    : option.code
                }
                value={null}
                inputValue={hcpcsInputValue}
                onChange={(_, value) => {
                  if (value) {
                    addHCPCSCode({
                      id: `${Date.now()}-${Math.random()}`,
                      codeId: value.id,
                      code: value.code,
                      description:
                        value.description || `HCPCS Code ${value.code}`,
                    })
                    setHcpcsInputValue('')
                  }
                }}
                onInputChange={(_, value) => {
                  setHcpcsInputValue(value)
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select HCPCS Level II Code"
                    placeholder="Search HCPCS codes..."
                  />
                )}
                loading={loadingHcpcs}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
              />
            </Grid>
          </Grid>

          {/* Selected HCPCS Codes */}
          {formData.clinicalCoding.hcpcsLevel.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected HCPCS Level II Codes:
              </Typography>
              {formData.clinicalCoding.hcpcsLevel.map((hcpcsCode, index) => (
                <Card
                  key={hcpcsCode.id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">
                      {hcpcsCode.code}
                    </Typography>
                    {hcpcsCode.description && (
                      <Typography variant="body2" color="text.secondary">
                        {hcpcsCode.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      onClick={() => removeHCPCSCode(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Modifier Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent id="modifier-section">
          <Typography variant="h6" gutterBottom>
            Modifier (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a modifier code for your service.
          </Typography>

          {modifierFetchError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {modifierFetchError}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={modifierOptions}
                getOptionLabel={option =>
                  option.description
                    ? `${option.code} - ${option.description}`
                    : option.code
                }
                value={selectedModifierOption}
                inputValue={modifierInputValue}
                onChange={(_, value) => {
                  handleModifierChange(value)
                }}
                onInputChange={(_, value, reason) => {
                  setModifierInputValue(value)
                  if (reason === 'clear') {
                    handleModifierChange(null)
                  }
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select Modifier"
                    placeholder="Search modifiers..."
                  />
                )}
                loading={loadingModifiers}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false
                  return option.code === value.code && option.id === value.id
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Place of Service Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent id="pos-section">
          <Typography variant="h6" gutterBottom>
            Place of Service (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the place of service code for your service.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel id="pos-select-label">Place of Service</InputLabel>
                <Select
                  labelId="pos-select-label"
                  id="pos-select"
                  value={formData.clinicalCoding.pos || ''}
                  label="Place of Service"
                  onChange={e => handlePosChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {posOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Medical Necessity Note */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Medical Necessity Note
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide a brief explanation of medical necessity for these services
            (optional, max 1000 characters).
          </Typography>

          {medicalNecessityError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {medicalNecessityError}
            </Alert>
          )}

          <TextField
            id="medical-necessity-field"
            multiline
            rows={4}
            fullWidth
            label="Medical Necessity Note"
            placeholder="Describe the medical necessity for the services included in this package..."
            value={formData.clinicalCoding.medicalNecessityNote}
            onChange={e =>
              onUpdate({
                clinicalCoding: {
                  ...formData.clinicalCoding,
                  medicalNecessityNote: e.target.value,
                },
              })
            }
            error={!!medicalNecessityError}
            helperText={
              medicalNecessityError ||
              `${formData.clinicalCoding.medicalNecessityNote.length}/1000 characters`
            }
            inputProps={{ maxLength: 1000 }}
          />

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="info" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              This note helps justify the medical necessity of your services to
              insurance providers.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
